import { formatInventoryUnitPrice } from "../../utils/formatInventoryUnitPrice";
import {
  formatQty,
  placementTags,
  productCostView,
  type InventoryProductRow,
} from "../../utils/inventoryProducts";
import { COST_UNKNOWN } from "../../utils/unknownCost";
import { ItemThumb } from "../ItemThumb";

/** "5 Oct", or "5 Oct 25" for a year that is not this one. */
function formatLastIn(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "—";
  const date = new Date(ms);
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: "2-digit" as const }),
  }).format(date);
}

/**
 * G-132 — one PRODUCT, one row, at every width.
 *
 * Replaces the per-zone, per-shop card (G-92) and the per-zone desktop tables.
 * The big number is the total (or the chosen zone's quantity), on the same line
 * as the name so the two can never be scrolled apart; the tags under the name
 * say where the rest is. Above 900 px the same row widens into a grid that adds
 * the cost and the last stock-in — CSS only, no second component.
 */
export function InventoryStockCard({
  product,
  shownQty,
  byShop,
  isLow,
  onOpen,
  onThumbnailClick,
}: {
  product: InventoryProductRow;
  /** What the big number shows: the total, or the chosen zone's share of it. */
  shownQty: number;
  /** Tag by shop (the view spans several) rather than by zone. */
  byShop: boolean;
  /** On the low-stock list: the total turns to the warning colour. */
  isLow?: boolean;
  onOpen: (product: InventoryProductRow) => void;
  onThumbnailClick: (item: { id: string; name: string; imageKey?: string | null }) => void;
}) {
  const { item } = product;
  const qtyText = formatQty(shownQty);
  const tags = placementTags(product, byShop);
  // WO-B part 6 (OD-198): the average speaks for the known units only.
  const cost = productCostView(product);

  return (
    <div className="inv-card">
      {/* Outside the button: tapping a photo enlarges it, and a button inside a
          button is not valid HTML. */}
      <div className="inv-card-thumb">
        <ItemThumb
          itemId={item.id}
          imageKey={item.imageKey ?? null}
          alt={item.name}
          onThumbnailClick={() => onThumbnailClick(item)}
        />
      </div>

      <button type="button" className="inv-card-body" onClick={() => onOpen(product)}>
        <span className="inv-card-name">{item.name}</span>
        <span className="inv-card-meta">
          {tags.map((tag) => (
            <span key={tag.key} className="inv-card-tag">
              {tag.label} <b>{formatQty(tag.qty)}</b>
            </span>
          ))}
        </span>
      </button>

      <span className="inv-card-cost">
        {cost.unitCostEur != null
          ? formatInventoryUnitPrice(String(cost.unitCostEur), item.defaultUom)
          : cost.costUnknown
            ? COST_UNKNOWN
            : "no cost yet"}
        {cost.uncountedNote ? <small className="inv-card-cost-note">{cost.uncountedNote}</small> : null}
      </span>
      <span className="inv-card-lastin">Last in {formatLastIn(product.lastStockInAt)}</span>

      <button
        type="button"
        className={`inv-card-qty${isLow ? " is-low" : ""}`}
        onClick={() => onOpen(product)}
        aria-label={`${qtyText} ${item.defaultUom} of ${item.name}${isLow ? ", low stock" : ""}`}
      >
        <b>{qtyText}</b>
        <span>{item.defaultUom}</span>
      </button>
    </div>
  );
}
